import { useCallback, useEffect, useState } from 'react';
import { useFetcher } from '@remix-run/react';
import {
    ACTION_GET_DESIGN_BOARD_LIST,
    type loader as GetDesignBoardListRemixLoader,
} from '~/routes/actions.get-design-board-list';
import {
    type action as CreateDesignBoardRemixAction,
    ACTION_CREATE_DESIGN_BOARD,
} from '~/routes/actions.create-design-board';
import {
    type action as AddItemsToDesignBoardRemixAction,
    ACTION_ADD_ITEMS_TO_DESIGN_BOARD,
} from '~/routes/actions.add-items-to-design-board';
import {
    type action as AddItemsToMoodBoardRemixAction,
    ACTION_ADD_ITEMS_TO_MOOD_BOARD,
} from '~/routes/actions.add-items-to-mood-board';
import type { GetDesignBoardListItemResponse } from '@modules-rocco/design-board/api/get-design-board-list.server';
import type { DesignBoardsTrackingConfig } from '@rocco/ui/design-board/components/DesignBoardsActions';
import { useTracker } from '@archipro-website/tracker';
import { useUser } from '~/modules/user';
import {
    ACTION_GET_MOOD_BOARD_LIST,
    type loader as GetMoodBoardListRemixLoader,
} from '~/routes/actions.get-mood-board-list';
import { ACTION_CREATE_MOOD_BOARD } from '~/routes/actions.create-mood-board';

import {
    BUILDHUB_PROJECTS_API,
    type loader as GetBuildHubsListRemixLoader,
} from '~/routes/remix-api.build-hub.projects';
import type { BuildHubForGrouping } from '@rocco/ui/design-board/components/SelectDesignBoardModal';
import { useFeature } from '~/modules/root/hook/use-feature';

import { type deleteDesignBoardPins as RemoveDesignBoardPins } from '@modules/design-board/api/design-board-and-pins-api-functions/pin-functions/deleteDesignBoardPins';
import { type action as RemoveItemsFromMoodBoard } from '~/routes/remix-api.app.build-hub-inspiration-items-remove';
import { REMOVE_INSPIRATION_ITEMS_BY_PAGE_API_ROUTE } from '~/routes/actions.remove-items-from-mood-board-by-page';
import { REMOVE_DESIGN_BOARD_ITEMS_BY_PAGE_API_ROUTE } from '~/routes/actions.remove-items-from-design-board-by-page';

/**
 * Main hook for managing design boards functionality. Combines UI API actions and states.
 * @returns {Object} An object containing:
 * - isLoading: Boolean indicating if any operation is in progress
 * - boards: Array of design board data
 * - createdBoardData: Data from the most recent board creation
 * - addedItemsData: Data from the most recent items addition
 * - handleFetchBoards: Function to fetch all boards
 * - handleCreateBoard: Function to create a new board
 * - handleAddItemsToBoard: Function to add items to a board
 * - trackingConfig: Tracking config for design boards actions
 * - createBoardError: Error message if board creation failed
 * - resetCreateBoardError: Function to reset the create board error state
 */
export const useDesignBoards = () => {
    const [boards, isFetchingBoards, handleFetchBoards] =
        useGetDesignBoardList();
    const [
        createdBoardData,
        isCreatingBoard,
        handleCreateBoard,
        createBoardError,
        resetCreateBoardError,
    ] = useCreateDesignBoard();
    const [addedItemsData, isAddingItems, handleAddItemsToBoard] =
        useAddItemsToDesignBoard();
    const trackingConfig = useDesignBoardsTracking();
    const [moodBoards, isFetchingMoodBoards, handleFetchMoodBoards] =
        useGetMoodBoardList();
    const [buildHubs, isFetchingBuildHubs, handleFetchBuildHubs] =
        useGetBuildHubsList();
    const [
        addedMoodBoardData,
        isAddingMoodBoardItems,
        handleAddItemsToMoodBoard,
    ] = useAddItemsToMoodBoard();
    const [removedBoardData, isRemovingBoardItems, handleRemoveItemsFromBoard] =
        useRemoveItemsFromBoard();
    const [
        removedMoodBoardData,
        isRemovingMoodBoardItems,
        handleRemoveItemsFromMoodBoard,
    ] = useRemoveItemsFromMoodBoard();

    return {
        isLoading:
            isFetchingBoards ||
            isCreatingBoard ||
            isAddingItems ||
            isFetchingMoodBoards ||
            isFetchingBuildHubs ||
            isAddingMoodBoardItems ||
            isRemovingBoardItems ||
            isRemovingMoodBoardItems,
        boards,
        createdBoardData,
        addedItemsData,
        handleFetchBoards,
        handleCreateBoard,
        handleAddItemsToBoard,
        trackingConfig,
        createBoardError,
        resetCreateBoardError,

        //mood boards
        moodBoards,
        handleFetchMoodBoards,
        addedMoodBoardData,
        handleAddItemsToMoodBoard,
        removedMoodBoardData,
        handleRemoveItemsFromMoodBoard,
        removedBoardData,
        handleRemoveItemsFromBoard,

        // build hubs
        buildHubs,
        handleFetchBuildHubs,
    };
};

/**
 * Hook for fetching the list of design boards.
 * @returns A tuple containing:
 * - Array of design board data or undefined if not yet loaded
 * - Boolean indicating if fetch is in progress
 * - Function to trigger boards fetch
 */
const useGetDesignBoardList = (): [
    GetDesignBoardListItemResponse[] | undefined,
    boolean,
    () => void,
] => {
    const fetcher = useFetcher<typeof GetDesignBoardListRemixLoader>();

    const handleFetchBoards = useCallback(() => {
        fetcher.load(ACTION_GET_DESIGN_BOARD_LIST);
    }, [fetcher]);

    return [fetcher.data, fetcher.state !== 'idle', handleFetchBoards];
};

/**
 * Hook for fetching the list of mood boards the user owns and have been invited to collaborate on with edit permission.
 */
const useGetMoodBoardList = (): [
    GetDesignBoardListItemResponse[] | undefined,
    boolean,
    () => void,
] => {
    const fetcher = useFetcher<typeof GetMoodBoardListRemixLoader>();

    const handleFetchBoards = useCallback(() => {
        fetcher.load(`${ACTION_GET_MOOD_BOARD_LIST}?canEdit=true`);
    }, [fetcher]);

    return [fetcher.data, fetcher.state !== 'idle', handleFetchBoards];
};

/**
 * Hook for fetching the list of BuildHubs (projects) the user owns or has access to.
 * Maps BuildHubViewFragment to BuildHubForGrouping for use in rocco components.
 */
const useGetBuildHubsList = (): [
    BuildHubForGrouping[] | undefined,
    boolean,
    () => void,
] => {
    const isBuildHubEnabled = useFeature('buildhub');

    const fetcher = useFetcher<typeof GetBuildHubsListRemixLoader>();

    const handleFetchBuildHubs = useCallback(() => {
        if (isBuildHubEnabled) {
            fetcher.load(BUILDHUB_PROJECTS_API);
        }
    }, [fetcher, isBuildHubEnabled]);

    // Map BuildHubViewFragment to BuildHubForGrouping
    // Type assertion needed because fetcher.data type is inferred from loader return type
    const mappedData = fetcher.data?.nodes.map((buildHub) => ({
        ID: (buildHub as { ID: string | number }).ID,
        Title: (buildHub as { Title: string }).Title,
    }));

    return [mappedData, fetcher.state !== 'idle', handleFetchBuildHubs];
};

/**
 * Hook for creating a new design board.
 * @returns A tuple containing:
 * - Data from the most recent board creation
 * - Boolean indicating if creation is in progress
 * - Function to create a new board with specified title, optional description and privacy setting
 * - Error message if creation failed
 * - Function to reset the error state
 */
const useCreateDesignBoard = () => {
    const fetcher = useFetcher<typeof CreateDesignBoardRemixAction>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleCreateBoard = useCallback(
        (
            title: string,
            description?: string,
            isPrivate?: boolean,
            buildHubId?: number
        ) => {
            // Clear any previous error before a new attempt
            setErrorMessage(null);

            if (buildHubId && buildHubId > 0) {
                fetcher.submit(
                    {
                        buildHubId: buildHubId.toString(),
                        title: title,
                        description: description || '',
                        isPrivate: isPrivate?.toString() || 'true',
                    },
                    {
                        method: 'post',
                        action: ACTION_CREATE_MOOD_BOARD,
                    }
                );
            } else {
                fetcher.submit(
                    {
                        title,
                        description: description || '',
                        isPrivate: isPrivate?.toString() || 'true',
                    },
                    {
                        method: 'post',
                        action: ACTION_CREATE_DESIGN_BOARD,
                    }
                );
            }
        },
        [fetcher]
    );

    const resetError = useCallback(() => {
        // Locally reset the error message without triggering network calls
        setErrorMessage(null);
    }, []);

    useEffect(() => {
        if (!fetcher.data) {
            setErrorMessage(null);
            return;
        }

        // Only process error messages, don't clear on successful responses
        const data = fetcher.data;

        // Check for our new error response format: { error: true, message: "..." }
        if (typeof data === 'object' && data !== null && 'error' in data) {
            const errorData = data as { error: boolean; message?: string };
            if (errorData.error === true && errorData.message) {
                setErrorMessage(errorData.message);
                return;
            }
        }

        // Check for ServerErrorResponse format: { _ap_error: true, message: "..." }
        if (typeof data === 'object' && data !== null && '_ap_error' in data) {
            const errorData = data as { _ap_error: boolean; message?: string };
            if (errorData._ap_error === true && errorData.message) {
                setErrorMessage(errorData.message);
                return;
            }
        }

        // Check if the data itself is an error message (string)
        if (typeof data === 'string') {
            setErrorMessage(data);
            return;
        }

        // For successful responses, don't automatically clear the error
        // The error will be cleared when a new request starts (in handleCreateBoard)
    }, [fetcher.data]);

    return [
        fetcher.data,
        fetcher.state !== 'idle',
        handleCreateBoard,
        errorMessage,
        resetError,
    ];
};

/**
 * Hook for adding items to an existing design board.
 * @returns A tuple containing:
 * - Data from the most recent items addition
 * - Boolean indicating if addition is in progress
 * - Function to add items to a board, requiring board ID, type of items, and array of item IDs
 */
const useAddItemsToDesignBoard = () => {
    const fetcher = useFetcher<typeof AddItemsToDesignBoardRemixAction>();

    const handleAddItemsToDesignBoard = useCallback(
        (boardId: number, itemsType: string, items: string[]) => {
            // Ensure items is always sent as array by using FormData
            const formData = new FormData();
            formData.append('boardId', boardId.toString());
            formData.append('itemsType', itemsType);

            items.forEach((item) => {
                formData.append('itemIds[]', item);
            });

            fetcher.submit(formData, {
                method: 'post',
                action: ACTION_ADD_ITEMS_TO_DESIGN_BOARD,
            });
        },
        [fetcher]
    );

    return [
        fetcher.data,
        fetcher.state !== 'idle',
        handleAddItemsToDesignBoard,
    ];
};

const useAddItemsToMoodBoard = () => {
    const fetcher = useFetcher<typeof AddItemsToMoodBoardRemixAction>();

    const handleAddItemsToMoodBoard = useCallback(
        (boardId: number, itemsType: string, items: string[]) => {
            // Ensure items is always sent as array by using FormData
            const formData = new FormData();
            formData.append('boardId', boardId.toString());
            formData.append('itemsType', itemsType);

            items.forEach((item) => {
                formData.append('itemIds[]', item);
            });

            fetcher.submit(formData, {
                method: 'post',
                action: ACTION_ADD_ITEMS_TO_MOOD_BOARD,
            });
        },
        [fetcher]
    );

    return [fetcher.data, fetcher.state !== 'idle', handleAddItemsToMoodBoard];
};

const useRemoveItemsFromBoard = () => {
    const fetcher = useFetcher<typeof RemoveDesignBoardPins>();

    const handleRemoveItemsFromBoard = useCallback(
        (boardId: number, items: string[]) => {
            const formData = new FormData();
            formData.append('boardId', boardId.toString());
            items.forEach((id) => {
                formData.append('pageIds[]', id);
            });

            fetcher.submit(formData, {
                method: 'post',
                action: REMOVE_DESIGN_BOARD_ITEMS_BY_PAGE_API_ROUTE,
            });
        },
        [fetcher]
    );

    return [fetcher.data, fetcher.state !== 'idle', handleRemoveItemsFromBoard];
};

const useRemoveItemsFromMoodBoard = () => {
    const fetcher = useFetcher<typeof RemoveItemsFromMoodBoard>();

    const handleRemoveItemsFromMoodBoard = useCallback(
        (boardId: number, items: string[]) => {
            const formData = new FormData();
            formData.append('boardId', boardId.toString());
            items.forEach((id) => {
                formData.append('pageIds[]', id);
            });

            fetcher.submit(formData, {
                method: 'post',
                action: REMOVE_INSPIRATION_ITEMS_BY_PAGE_API_ROUTE,
            });
        },
        [fetcher]
    );

    return [
        fetcher.data,
        fetcher.state !== 'idle',
        handleRemoveItemsFromMoodBoard,
    ];
};

/**
 * Hook that provides tracking configuration for design board interactions.
 * Returns a config object with callbacks for tracking
 * @returns {DesignBoardsTrackingConfig} Tracking configuration object
 */
const useDesignBoardsTracking = (): DesignBoardsTrackingConfig => {
    const tracker = useTracker();
    const user = useUser();
    const guestID = user.__typename === 'Me' ? user.TrackedGuest?.ID : 0;

    return {
        onAfterBoardSelect: (board, source) => {
            if (source || board.isNew) {
                tracker.log('DesignBoardCreateSubmit', {
                    url: new URL(window.location.href),
                    data: source ? { Source: source } : undefined,
                });
            }
        },
        onBeforeFollow: (itemId, source) => {
            tracker.log('PinMenuAdd', {
                url: new URL(window.location.href),
                data: {
                    LibraryItemID: itemId,
                    ...(source && { Source: source }),
                },
            });
        },
        onAfterFollow: (itemId, source) => {
            tracker.log('SaveSubmit', {
                url: new URL(window.location.href),
                data: {
                    LibraryItemID: itemId,
                    guest_id: guestID,
                    ...(source && { Source: source }),
                },
            });
        },
    };
};
